import { 
  getAuth, 
  signInWithCustomToken, 
  onAuthStateChanged,
  User,
  Auth
} from 'firebase/auth';
import app from '../firebase';
import { useAuthStore } from '../../store/useAuthStore';
import { useUserStore } from '../../store/useUserStore';
import { useTenantStore } from '../../store/useTenantStore';
import { generateDeviceId, getDeviceInfo } from '../../utils/deviceInfo';
import { apiClient } from '../api/client';

// Initialize Firebase Auth
const auth: Auth = getAuth(app);

// Custom token creation (this should be done on your backend)
export interface CustomTokenPayload {
  uid: string;
  tenantId: number;
  email?: string;
  name?: string;
}

// Store Firebase tokens
interface FirebaseTokens {
  firebaseToken: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt?: number;
}

let firebaseTokens: FirebaseTokens | null = null;
let isFetchingToken = false;

// Get Firebase custom token from backend
async function getFirebaseCustomToken(): Promise<string> {
  console.log('🔄 Getting new Firebase custom token from backend...');
  
  if (isFetchingToken) {
    console.log('⏳ Already fetching Firebase token, waiting...');
    // Wait for the current fetch to complete
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (!isFetchingToken && firebaseTokens) {
          clearInterval(checkInterval);
          resolve(firebaseTokens.firebaseToken);
        }
      }, 100);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Timeout waiting for Firebase token'));
      }, 10000);
    });
  }
  
  isFetchingToken = true;
  
  try {
    const userStore = useUserStore.getState();
    const deviceInfo = getDeviceInfo();
    
    // Use API client which handles tenant URL and proxy automatically
    const response = await apiClient.post('/api/WhiteboardFirebase/GetToken', {
      deviceId: generateDeviceId(),
      deviceName: `${deviceInfo.platform} - ${deviceInfo.vendor}`,
      userId: userStore.userInfo?.id.toString() || ''
    });
    
    const tokens = {
      ...response.data.result,
      expiresAt: Date.now() + (response.data.result.expiresIn * 1000)
    };
    
    firebaseTokens = tokens;
    
    // Store refresh token securely
    localStorage.setItem('tahta_firebase_refresh', tokens.refreshToken);
    
    isFetchingToken = false;
    return tokens.firebaseToken;
  } catch (error) {
    console.error('Error getting Firebase token:', error);
    isFetchingToken = false;
    throw error;
  }
}

// Refresh Firebase token
async function refreshFirebaseToken(): Promise<string> {
  try {
    const storedRefreshToken = localStorage.getItem('tahta_firebase_refresh');
    if (!storedRefreshToken) {
      throw new Error('No refresh token available');
    }
    
    // Use API client which handles tenant URL and proxy automatically
    const response = await apiClient.post('/api/WhiteboardFirebase/RefreshToken', {
      refreshToken: storedRefreshToken,
      deviceId: generateDeviceId()
    });
    
    const tokens = {
      ...response.data.result,
      expiresAt: Date.now() + (response.data.result.expiresIn * 1000)
    };
    
    firebaseTokens = tokens;
    
    // Update stored refresh token
    localStorage.setItem('tahta_firebase_refresh', tokens.refreshToken);
    
    return tokens.firebaseToken;
  } catch (error) {
    console.error('Error refreshing Firebase token:', error);
    // Clear invalid refresh token
    localStorage.removeItem('tahta_firebase_refresh');
    firebaseTokens = null;
    throw error;
  }
}

// Get valid Firebase token (with auto-refresh)
async function getValidFirebaseToken(): Promise<string> {
  // Check if we have tokens and they're still valid
  if (firebaseTokens && firebaseTokens.expiresAt) {
    const now = Date.now();
    const expiryBuffer = 5 * 60 * 1000; // 5 minutes buffer
    
    if (firebaseTokens.expiresAt - now > expiryBuffer) {
      return firebaseTokens.firebaseToken;
    }
    
    // Token is expiring soon, refresh it
    try {
      return await refreshFirebaseToken();
    } catch (error) {
      // Refresh failed, get new token
      return await getFirebaseCustomToken();
    }
  }
  
  // No tokens, get new ones
  return await getFirebaseCustomToken();
}

// Sign in to Firebase with your app's authentication
export async function signInToFirebase(): Promise<User | null> {
  try {
    console.log('🔐 Starting Firebase sign in...');
    const authStore = useAuthStore.getState();
    
    if (!authStore.accessToken) {
      console.error('❌ No access token available');
      return null;
    }
    
    console.log('📱 Access token found, getting Firebase token...');
    try {
      const customToken = await getValidFirebaseToken();
      console.log('🔑 Got Firebase custom token');
      const credential = await signInWithCustomToken(auth, customToken);
      
      // Set up token refresh timer
      setupTokenRefreshTimer();
      
      return credential.user;
    } catch (error) {
      console.error('Firebase authentication failed:', error);
      // Clear tokens on auth failure
      firebaseTokens = null;
      localStorage.removeItem('tahta_firebase_refresh');
      return null;
    }
  } catch (error) {
    console.error('Firebase sign in error:', error);
    return null;
  }
}

// Sign out from Firebase
export async function signOutFromFirebase(): Promise<void> {
  try {
    await auth.signOut();
    // Clear Firebase tokens
    firebaseTokens = null;
    localStorage.removeItem('tahta_firebase_refresh');
    // Clear refresh timer
    clearTokenRefreshTimer();
  } catch (error) {
    console.error('Firebase sign out error:', error);
  }
}

// Token refresh timer
let tokenRefreshTimer: NodeJS.Timeout | null = null;

function setupTokenRefreshTimer(): void {
  clearTokenRefreshTimer();
  
  if (!firebaseTokens || !firebaseTokens.expiresAt) return;
  
  // Refresh token 10 minutes before expiry
  const refreshTime = firebaseTokens.expiresAt - Date.now() - (10 * 60 * 1000);
  
  if (refreshTime > 0) {
    tokenRefreshTimer = setTimeout(async () => {
      try {
        const newToken = await refreshFirebaseToken();
        // Re-authenticate with new token
        await signInWithCustomToken(auth, newToken);
        // Set up next refresh
        setupTokenRefreshTimer();
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Try full re-authentication
        await signInToFirebase();
      }
    }, refreshTime);
  }
}

function clearTokenRefreshTimer(): void {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
}

// Auth state listener
export function onFirebaseAuthStateChanged(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

// Get current Firebase user
export function getCurrentFirebaseUser(): User | null {
  return auth.currentUser;
}

// Wait for Firebase authentication to be ready
export async function waitForFirebaseAuth(timeout: number = 5000): Promise<User | null> {
  const startTime = Date.now();
  
  // First check if already authenticated
  if (auth.currentUser) {
    return auth.currentUser;
  }
  
  // Wait for auth state to change
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsubscribe();
        resolve(user);
      } else if (Date.now() - startTime > timeout) {
        unsubscribe();
        resolve(null);
      }
    });
    
    // Also set a timeout
    setTimeout(() => {
      unsubscribe();
      resolve(auth.currentUser);
    }, timeout);
  });
}

// Check if user is authenticated
export function isFirebaseAuthenticated(): boolean {
  return auth.currentUser !== null;
}

// Get ID token for authenticated requests
export async function getFirebaseIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    
    return await user.getIdToken();
  } catch (error) {
    console.error('Error getting Firebase ID token:', error);
    return null;
  }
}

// Refresh ID token
export async function refreshFirebaseIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    
    return await user.getIdToken(true); // Force refresh
  } catch (error) {
    console.error('Error refreshing Firebase ID token:', error);
    return null;
  }
}

// Initialize Firebase auth when app auth changes
export async function initializeFirebaseAuth(): Promise<User | null> {
  // Listen to auth state changes in your app
  const authStore = useAuthStore.getState();
  
  // Sign in to Firebase when app auth is available
  let user: User | null = null;
  if (authStore.accessToken) {
    try {
      user = await signInToFirebase();
      console.log('✅ Firebase auth initialized successfully');
    } catch (error) {
      console.error('❌ Firebase auth initialization failed:', error);
    }
  }
  
  // Set up listener for auth changes
  useAuthStore.subscribe((state, prevState) => {
    if (state.accessToken && !prevState.accessToken) {
      // User logged in
      signInToFirebase().catch(console.error);
    } else if (!state.accessToken && prevState.accessToken) {
      // User logged out
      signOutFromFirebase().catch(console.error);
    }
  });
  
  return user;
}

export default auth;